import { useState } from 'react'
import { Button, Card, Icon, Tag, Text, Title } from '@nimbus-ds/components'
import { StatsIcon } from '@nimbus-ds/icons'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { Experiment, ExperimentResults } from '@/types'

/**
 * NÚMEROS — stats layer. The only place on the page where the full statistical vocabulary lives:
 * posteriors, thresholds, ROPE, Newcombe CIs, precision labels, SRM, ETA mechanics. Collapsed by
 * default; a device selector (Todos / Mobile / Desktop) switches to the per-segment engine numbers.
 */

interface StatsDetailCardProps {
  experiment: Experiment | null
  results: ExperimentResults | null
}

type DeviceView = 'all' | 'mobile' | 'desktop'

const DEVICE_LABEL: Record<Exclude<DeviceView, 'all'>, string> = { mobile: 'Mobile', desktop: 'Desktop' }
const PRECISION_LABEL: Record<string, string> = {
  precise: 'precisa',
  approximate: 'aproximada',
  direction_only: 'solo dirección',
  unknown: '—',
}

const fmtPP = (v: number) => `${v > 0 ? '+' : ''}${(v * 100).toFixed(2)}pp`
const fmtPct = (v: number | null | undefined, d = 1) => (v == null ? '—' : `${(v * 100).toFixed(d)}%`)

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <Text as="span">{label}</Text>
        {hint && <Text fontSize="caption" className="truncate">{hint}</Text>}
      </div>
      <span className="shrink-0 font-mono text-sm tabular-nums">{value}</span>
    </div>
  )
}

export function StatsDetailCard({ experiment, results }: StatsDetailCardProps) {
  const [view, setView] = useState<DeviceView>('all')
  const r = results as any
  if (!r || !r.sessions || !r.conversion_rate) return null

  const isNegative = Boolean(experiment?.is_negative || r.is_negative)
  const goalLabel: string = r.summary?.primary_metric?.label || 'Compra'

  const segments = r.by_device?.present ? r.by_device.segments : undefined
  const deviceOptions: DeviceView[] = [
    'all',
    ...(segments?.mobile ? (['mobile'] as const) : []),
    ...(segments?.desktop ? (['desktop'] as const) : []),
  ]

  // Hypothesis-vs-baseline framing (control IS the hypothesis in a negative experiment).
  const pHypOf = (bb: { p_better: number | null; p_worse: number | null } | null | undefined) =>
    bb == null ? null : isNegative ? bb.p_worse : bb.p_better
  const pBaseOf = (bb: { p_better: number | null; p_worse: number | null } | null | undefined) =>
    bb == null ? null : isNegative ? bb.p_better : bb.p_worse

  // --- goal rung numbers (headline engine) ---
  let displayLift = (r.lift ?? 0) * 100
  if (isNegative) {
    const ctrlRate = r.conversion_rate?.control ?? 0
    const trtRate = r.conversion_rate?.treatment ?? 0
    displayLift = trtRate > 0 ? ((ctrlRate - trtRate) / trtRate) * 100 : -displayLift
  }
  const ci = r.summary?.lift?.confidence_interval as [number, number] | undefined
  const ciPP = ci ? (isNegative ? [-ci[1] * 100, -ci[0] * 100] : [ci[0] * 100, ci[1] * 100]) : null
  const goalPrecision = r.summary?.lift?.precision?.level as string | undefined

  // --- purchase rung (guardrail) ---
  const g = r.guardrail
  const gci = g?.lift?.confidence_interval
  const gciPP = gci ? (isNegative ? [-gci.upper * 100, -gci.lower * 100] : [gci.lower * 100, gci.upper * 100]) : null

  const eta = r.decision_eta

  const allView = (
    <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
      <div className="divide-y">
        <Text fontSize="caption" fontWeight="medium" className="pb-1">
          Efecto — objetivo ({goalLabel})
        </Text>
        <Row label={`Lift${isNegative ? ' (hipótesis)' : ''}`} value={`${displayLift > 0 ? '+' : ''}${displayLift.toFixed(2)}%`} hint="relativo, hipótesis vs. línea base" />
        <Row label="Diferencia absoluta" value={fmtPP((r.summary?.lift?.absolute ?? 0) * (isNegative ? -1 : 1))} />
        {ciPP && (
          <Row
            label="IC 95% de la diferencia"
            value={`[${fmtPP(ciPP[0] / 100)}, ${fmtPP(ciPP[1] / 100)}]`}
            hint={`Newcombe (score) · precisión: ${PRECISION_LABEL[goalPrecision ?? 'unknown']}`}
          />
        )}
        {r.summary?.lift?.asymmetry?.leader && (
          <Row
            label="Asimetría del IC"
            value={`peor ${fmtPP(r.summary.lift.asymmetry.worst_case ?? 0)} · mejor ${fmtPP(r.summary.lift.asymmetry.best_case ?? 0)}`}
            hint={`lado favorito: ${r.summary.lift.asymmetry.leader}${r.summary.lift.asymmetry.actionable ? ' · sin pérdida al umbral de la postura — aplicable' : ''}`}
          />
        )}
        {g?.bb && (
          <>
            <Text fontSize="caption" fontWeight="medium" className="pb-1 pt-3">
              Efecto — compras (guardarraíl)
            </Text>
            {g.lift && (
              <Row
                label="Diferencia absoluta"
                value={fmtPP(g.lift.absolute * (isNegative ? -1 : 1))}
                hint="tasa de compra, hipótesis vs. línea base"
              />
            )}
            {gciPP && (
              <Row
                label="IC 95% de la diferencia"
                value={`[${fmtPP(gciPP[0] / 100)}, ${fmtPP(gciPP[1] / 100)}]`}
                hint={`Newcombe (score) · precisión: ${PRECISION_LABEL[g.lift?.precision?.level ?? 'unknown']}`}
              />
            )}
            {g.lift?.asymmetry?.leader && (
              <Row
                label="Asimetría del IC"
                value={`peor ${fmtPP(g.lift.asymmetry.worst_case ?? 0)} · mejor ${fmtPP(g.lift.asymmetry.best_case ?? 0)}`}
                hint={`lado favorito: ${g.lift.asymmetry.leader}${g.lift.asymmetry.actionable ? ' · sin pérdida al umbral de la postura — aplicable' : ''}`}
              />
            )}
          </>
        )}
      </div>
      <div className="divide-y">
        <Text fontSize="caption" fontWeight="medium" className="pb-1">
          Motor de decisión (bayesiano, BB+ROPE)
        </Text>
        <Row label={`P(hipótesis mejor) — ${goalLabel}`} value={fmtPct(pHypOf(r.bb_verdict ? { p_better: r.bb_p_better, p_worse: r.bb_p_worse } : null))} hint="más allá del margen ROPE" />
        <Row label="P(línea base mejor)" value={fmtPct(pBaseOf(r.bb_verdict ? { p_better: r.bb_p_better, p_worse: r.bb_p_worse } : null))} />
        {g?.bb && <Row label="P(hipótesis mejor) — compras" value={fmtPct(pHypOf(g.bb))} hint={`estado del guardarraíl: ${g.state}`} />}
        {r.bb_rope_width != null && <Row label="Margen (ROPE)" value={`±${(r.bb_rope_width * 100).toFixed(1)}%`} hint='diferencia relativa que se considera "igual"' />}
        {r.bb_threshold != null && <Row label="Umbral de decisión" value={`${(r.bb_threshold * 100).toFixed(0)}%`} hint={`postura ${r.risk_level ?? 'safe'}`} />}
        {r.summary?.decided_on && <Row label="Decidido por" value={r.summary.decided_on === 'guardrail' ? 'compras (guardarraíl)' : 'objetivo'} />}

        {eta && (
          <>
            <Text fontSize="caption" fontWeight="medium" className="pb-1 pt-3">
              Camino a la decisión (ETA)
            </Text>
            <Row label="Estado" value={eta.state} />
            {eta.state === 'wait' && eta.sessions_needed != null && (
              <Row
                label="Sesiones faltantes"
                value={`~${eta.sessions_needed.toLocaleString('es-ES')}`}
                hint={`~${Math.round(eta.pace_per_day).toLocaleString('es-ES')} sesiones/día · ~${Math.ceil(eta.days_estimated ?? 0)} días`}
              />
            )}
            <Row
              label="Horizonte"
              value={`${eta.horizon_days} días`}
              hint={eta.horizon_source === 'end_date' ? 'fecha de fin' : eta.horizon_source === 'session_limit' ? 'límite de sesiones' : 'default de la postura'}
            />
          </>
        )}

        <Text fontSize="caption" fontWeight="medium" className="pb-1 pt-3">
          Referencia frecuentista
        </Text>
        {r.p_value != null && <Row label="p-valor (z-test)" value={r.p_value.toFixed(4)} />}
        {r.srm_analysis && (
          <Row
            label="SRM (reparto de tráfico)"
            value={`p = ${r.srm_analysis.p_value.toFixed(3)}`}
            hint={r.srm_analysis.is_valid ? 'sin desbalance' : 'desbalance detectado'}
          />
        )}
      </div>
    </div>
  )

  const segmentView = (key: Exclude<DeviceView, 'all'>) => {
    const seg = segments?.[key]
    if (!seg) return null
    const metricLabel = r.by_device.metric === 'goal' ? goalLabel : 'tasa de compra'
    return (
      <div className="space-y-1 max-w-xl">
        <Text fontSize="caption">
          Métrica del segmento: <Text as="span" fontWeight="medium" color="neutral-textHigh">{metricLabel}</Text>
          {seg.role === 'exploratory' && ' · segmento exploratorio (muestra chica: leer con cautela)'}
        </Text>
        <div className="divide-y">
          <Row
            label="Control"
            value={`${seg.control.conversions.toLocaleString('es-ES')} / ${seg.control.sessions.toLocaleString('es-ES')} (${(seg.control.conversion_rate * 100).toFixed(2)}%)`}
          />
          <Row
            label="Tratamiento"
            value={`${seg.treatment.conversions.toLocaleString('es-ES')} / ${seg.treatment.sessions.toLocaleString('es-ES')} (${(seg.treatment.conversion_rate * 100).toFixed(2)}%)`}
          />
          {seg.lift && (
            <>
              <Row
                label="Diferencia absoluta"
                value={fmtPP(seg.lift.absolute * (isNegative ? -1 : 1))}
                hint="hipótesis vs. línea base"
              />
              <Row
                label="IC 95% de la diferencia"
                value={
                  isNegative
                    ? `[${fmtPP(-seg.lift.confidence_interval.upper)}, ${fmtPP(-seg.lift.confidence_interval.lower)}]`
                    : `[${fmtPP(seg.lift.confidence_interval.lower)}, ${fmtPP(seg.lift.confidence_interval.upper)}]`
                }
                hint={`Newcombe (score) · precisión: ${PRECISION_LABEL[seg.lift.precision?.level ?? 'unknown']}`}
              />
              {seg.lift.asymmetry?.leader && (
                <Row
                  label="Asimetría del IC"
                  value={`peor ${fmtPP(seg.lift.asymmetry.worst_case ?? 0)} · mejor ${fmtPP(seg.lift.asymmetry.best_case ?? 0)}`}
                  hint={`lado favorito: ${seg.lift.asymmetry.leader}${seg.lift.asymmetry.actionable ? ' · sin pérdida al umbral de la postura — aplicable' : ''}`}
                />
              )}
            </>
          )}
          <Row label="P(hipótesis mejor)" value={fmtPct(pHypOf(seg.bb_rope))} hint="más allá del margen ROPE" />
          <Row label="P(línea base mejor)" value={fmtPct(pBaseOf(seg.bb_rope))} />
          <Row label="Veredicto del segmento" value={seg.bb_rope.verdict} hint={seg.bb_rope.threshold != null ? `umbral ${(seg.bb_rope.threshold * 100).toFixed(0)}%` : undefined} />
          {seg.p_value != null && <Row label="p-valor (z-test)" value={seg.p_value.toFixed(4)} />}
          {seg.srm?.p_value != null && (
            <Row label="SRM del segmento" value={`p = ${seg.srm.p_value.toFixed(3)}`} hint={seg.srm.flagged ? 'desbalance detectado' : 'sin desbalance'} />
          )}
        </div>
      </div>
    )
  }

  return (
    <Collapsible>
      <Card>
        <CollapsibleTrigger asChild>
          <button type="button" className="flex w-full items-center justify-between px-4 py-3 text-left sm:px-6">
            <span className="flex items-center gap-2">
              <Icon source={<StatsIcon />} />
              <Title as="h4">Números</Title>
            </span>
            <Text fontSize="caption">estadística completa · ver / ocultar</Text>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card.Body>
            <div className="space-y-4">
              {deviceOptions.length > 1 && (
                <div className="flex items-center gap-2">
                  {deviceOptions.map((opt) => (
                    <Button
                      key={opt}
                      appearance={view === opt ? 'primary' : 'neutral'}
                      onClick={() => setView(opt)}
                    >
                      {opt === 'all' ? 'Todos' : DEVICE_LABEL[opt]}
                    </Button>
                  ))}
                  {view !== 'all' && <Tag appearance="neutral">segmento</Tag>}
                </div>
              )}
              {view === 'all' ? allView : segmentView(view)}
            </div>
          </Card.Body>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
